import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Form,
  Button,
  Select,
  Input,
  DatePicker,
  Row,
  Col,
  Spin,
  message,
  Drawer,
  Table,
} from "antd";
import {
  BankOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import CustomUploadNewBank from "@/hocComponents/customUploadNewBank";
import commonFn from "@/utils/commonFn";
import InsuranceVPH from "./InsuranceVPH"; 
const mergePropertyOptions = (list = []) => {
  const map = new Map();
  list.forEach((item) => {
    if (!item && item !== 0) return;
    if (
      item?.value === undefined ||
      item?.value === null ||
      item?.value === ""
    ) {
      return;
    }
    const key = String(item.value);
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

const InsuranceViewDetail = () => {
  const insuranceVPHRef = useRef(null);
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [statementFiles, setStatementFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [anetList, setAnetList] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [buildingLoadingMap, setBuildingLoadingMap] = useState({});
  const [editId, setEditId] = useState(null);
  const [glAcctOptionsMap, setGlAcctOptionsMap] = useState({});
  const [propLoading, setPropLoading] = useState(false);
  const [filePaths, setFilePaths] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const uploadRef = useRef();
  const [propId, setPropId] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [buildingData, setBuildingData] = useState([]);

  const getRecordId = () => {
    const params = new URLSearchParams(location.search);
    return params.get("id");
  };

  const columnStyle = {
    border: "1px solid #f0f0f0",
    padding: 20,
    borderRadius: 8,
    background: "#fafafa",
    minHeight: "100%",
  };

  const panelStyle = {
    ...columnStyle,
    height: "calc(100vh - 40px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const buildMap = (list = []) => {
    return list.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  };

  const mergePropOptions = (list = []) => {
    const map = new Map();
    list.forEach((item) => {
      if (!item?.value && item?.value !== 0) return;
      const key = String(item.value);
      if (!map.has(key)) {
        map.set(key, item);
      }
    });
    return Array.from(map.values());
  };
  const loadGlOptionsForProps = async (propIds) => {
  const uniquePropIds = [...new Set(propIds.filter(Boolean))];
  if (uniquePropIds.length === 0) return {}; 
  const glMap = {};
  // 并发请求所有未缓存的 propId
  const requests = uniquePropIds.map(async (propId) => {
    // 如果已有缓存，直接使用
    if (glCacheRef.current[propId]) {
      glMap[propId] = glCacheRef.current[propId];
      return;
    }
    
    try {
      const res = await http.get(api.getInstanceByGlAcct, {
        params: { propId },
      });
      const options = (res?.data || []).map((opt) => ({
        label: opt.glAcctTitle,
        value: opt.glChartId,
      }));
      glCacheRef.current[propId] = options;
      glMap[propId] = options;
    } catch (error) {
      console.error(`Failed to fetch GL for propId ${propId}:`, error);
      glCacheRef.current[propId] = [];
      glMap[propId] = [];
    }
  });
  
  await Promise.all(requests);
  return glMap;
};
  const loadData = async (id) => {
    if (!id) return;

    setLoading(true);
    try {
      const agentList = await getAgentTypeList();

      const res = await http.get(api.getInsuranceById, {
        params: { id },
      });

      const data = res?.data;
      if (!data) return;

      const buildingListData = (data.buildingList || []).map((item, index) => ({
        _id: index + 1,
        amount: item.amount ? commonFn.inputAmountFormat(String(item.amount), false, true) : "",
        propId: item.propId,
        propName: item.propName,
        glChartId: item.glChartId,
        glAcctTitle: item.glAcctTitle,
        liabilityDeductible: item.liabilityDeductible,
        liabilityLimit: item.liabilityLimit,
        trustName: item.trustName || item.owner,
        prop: item.prop,
        totalUnits: item.totalUnits,
        units: item.units,
        id: item.id,
        pushBatch: item.pushBatch,
        _isNew: false,
      }));

      setBuildingData(buildingListData);
       
      const uniquePropIds = [...new Set(buildingListData.map(item => item.propId).filter(Boolean))];
    let glMap = {};
    //  propId 数量超过 50 时，不请求 GL 数据
    if (uniquePropIds.length > 50) {
      buildingListData.forEach((item) => {
        if (!item.propId) return;
        if (!glMap[item.propId]) {
          glMap[item.propId] = [];
        }
        if (item.glChartId && item.glAcctTitle) {
          const hasCurrentOption = glMap[item.propId].some(opt => opt.value === item.glChartId);
          if (!hasCurrentOption) {
            glMap[item.propId].push({
              value: item.glChartId,
              label: item.glAcctTitle,
            });
          }
        }
      });
    } else {
      // propId 数量 <= 50 
      const propIds = buildingListData.map(item => item.propId);
      glMap = await loadGlOptionsForProps(propIds);
      buildingListData.forEach((item) => {
        if (!item.propId) return;
        const existingOptions = glMap[item.propId] || [];
        
        if (item.glChartId && item.glAcctTitle) {
          const hasCurrentOption = existingOptions.some(opt => opt.value === item.glChartId);
          if (!hasCurrentOption) {
            existingOptions.unshift({
              value: item.glChartId,
              label: item.glAcctTitle,
            });
          }
        }
        glMap[item.propId] = existingOptions;
      });
    }
      setGlAcctOptionsMap(glMap);

      const currentProps = buildingListData.map((item) => ({
        value: item.propId,
        label: item.propName,
      }));
      setPropId((prev) => mergePropOptions([...prev, ...currentProps]));

      const agentMap = buildMap(agentList);

      form.setFieldsValue({
        expirationDate: data.expirationDate ? dayjs(data.expirationDate) : null,
        effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate) : null,
        policyNumber: data.policyNumber,
        carrier: data.carrier,
        agent: data.agentVendorId ? String(data.agentVendorId) : undefined,
        payee: data.payeeVendorId ? String(data.payeeVendorId) : undefined,
        agentName: agentMap[String(data.agentVendorId)] || "",
        payeeName: agentMap[String(data.payeeVendorId)] || "",
      });

  //     if (data.insurancePaymentFileList?.length) {
  //       const files = data.insurancePaymentFileList.map((f) => ({
  //         uid: String(f.uuid || f.fileUploadId || f.id),
  //         uuid: String(f.uuid || f.fileUploadId || f.id),
  //         fileUploadId: f.fileUploadId,
  //          foreignId: f.foreignId,
  //         name: f.name || f.fileName, 
  //         fileName: f.name || f.fileName,
  //         status: "done",
  //         type: f.type,
  //          url: f.base64 || f.url,
  //         path: f.path,
  //         allPath: f.base64 || f.url, 
  //         foreignId: f.foreignId,
  //         isFromBackend: true,
  //       }));
  //       setStatementFiles(files);
  //       setFilePaths(files);
  //       setPreviewIndex(0);
  //         setTimeout(() => {
  //   if (uploadRef.current) {
  //     uploadRef.current.initUpdateList(files);
  //   }
  // }, 500);
  //     } else {
  //       setStatementFiles([]);
  //       setFilePaths([]);
  //       setPreviewIndex(-1);
  //     }
  // 统一处理所有文件
const allFiles = [];

// 处理支付文件
if (data.insurancePaymentFileList?.length) {
  const paymentFiles = data.insurancePaymentFileList.map((f) => ({
    uid: String(f.uuid || f.fileUploadId || f.id),
    uuid: String(f.uuid || f.fileUploadId || f.id),
    fileUploadId: f.fileUploadId,
    foreignId: f.foreignId,
    name: f.name || f.fileName,
    fileName: f.name || f.fileName,
    status: "done",
    type: f.type || "payment",
    url: f.base64 || f.url,
    path: f.path,
    allPath: f.base64 || f.url,
    foreignId: f.foreignId,
    isFromBackend: true,
  }));
  allFiles.push(...paymentFiles);
}

// 处理保单文件
if (data.insuranceFileList?.length) {
  const policyFiles = data.insuranceFileList.map((f) => ({
    uid: String(f.uuid || f.fileUploadId || f.id),
    uuid: String(f.uuid || f.fileUploadId || f.id),
    fileUploadId: f.fileUploadId,
    foreignId: f.foreignId,
    name: f.fileName || f.name,
    fileName: f.fileName || f.name,
    status: "done",
    type: f.type || "policy",
    url: f.url,  // 保单文件使用 url
    path: f.path,
    allPath: f.url,  // 保单文件使用 url
    foreignId: f.foreignId,
    isFromBackend: true,
  }));
  allFiles.push(...policyFiles);
}

// 一次性设置所有文件
if (allFiles.length > 0) {
  setStatementFiles(allFiles);
  setFilePaths(allFiles);
  setPreviewIndex(0);
  setTimeout(() => {
    if (uploadRef.current) {
      uploadRef.current.initUpdateList(allFiles);
    }
  }, 500);
} else {
  setStatementFiles([]);
  setFilePaths([]);
  setPreviewIndex(-1);
}
    } catch (err) {
      message.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = getRecordId();
    if (id) {
      setEditId(id);
      loadData(id);
    }
  }, [location.search]);
useEffect(() => {
  if (uploadOpen && filePaths.length > 0) {
    setTimeout(() => {
      if (uploadRef.current) {
        uploadRef.current.initUpdateList(filePaths);
      }
    }, 200);
  }
}, [uploadOpen]);
  const propAllRef = useRef([]);
  const propIdFun = async (content = "") => {
    try {
      setPropLoading(true);
      const response = await http.get(api.propSearch, {
        params: { content },
      });
      const data = response?.data || [];
      const formattedData = data.map((item) => ({
        value: item.propId,
        label: item.propName,
        prop: item.prop,
      }));
      if (!content) {
        propAllRef.current = formattedData;
        setPropId((prev) => mergePropOptions([...prev, ...formattedData]));
      } else {
        setPropId(formattedData);
      }
      return formattedData;
    } finally {
      setPropLoading(false);
    }
  };

  const propLoadedRef = useRef(false);
  const handleDropdownOpen = (open) => {
    if (!open) return;
    setPropId(propAllRef.current);
    if (propLoadedRef.current) return;
    propIdFun();
    propLoadedRef.current = true;
  };

  const searchTimer = useRef();
  const onTypeSearch = (value) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      propIdFun(value);
    }, 300);
  };

  const getPropDetailsFun = async (propIdValue, rowId) => {
    if (!propIdValue) return;
    setBuildingLoadingMap((prev) => ({
      ...prev,
      [rowId]: true,
    }));
    try {
      const detailRes = await http.get(api.getPropDetails, {
        params: { propId: propIdValue },
      });
      const detailData = detailRes?.data;
      if (detailData) {
        updateItem(rowId, {
          units: detailData.units || "",
          totalUnits: detailData.units || "",
          trustName: detailData.trustName || detailData.owner || "",
          propName: detailData.propName || "",
          prop: detailData.prop || "",
          liabilityDeductible: detailData.liabilityDeductible || "",
          liabilityLimit: detailData.liabilityLimit || "",
        });
      }
    } catch (err) {
      message.error("Failed to fetch property details");
    } finally {
      setBuildingLoadingMap((prev) => ({
        ...prev,
        [rowId]: false,
      }));
    }
  };

  const glCacheRef = useRef({});
  const fetchGlByProp = async (propIdValue) => {
    if (!propIdValue) return;
    if (glCacheRef.current[propIdValue]) {
      setGlAcctOptionsMap((prev) => ({
        ...prev,
        [propIdValue]: glCacheRef.current[propIdValue],
      }));
      return;
    }
    const res = await http.get(api.getInstanceByGlAcct, {
      params: { propId: propIdValue },
    });
    const options = (res?.data || []).map((item) => ({
      label: item.glAcctTitle,
      value: item.glChartId,
    }));
    glCacheRef.current[propIdValue] = options;
    setGlAcctOptionsMap((prev) => ({
      ...prev,
      [propIdValue]: options,
    }));
  };

  const updateItem = useCallback((id, updates) => {
    setBuildingData((prev) =>
      prev.map((item) => (item._id === id ? { ...item, ...updates } : item))
    );
  }, []);

  const removeItem = useCallback((id) => {
    setBuildingData((prev) => prev.filter((item) => item._id !== id));
  }, []);

  const addItem = useCallback(() => {
    const newId = Date.now();
    setBuildingData((prev) => [
      ...prev,
      {
        _id: newId,
        amount: "",
        propId: null,
        propName: "",
        glChartId: null,
        glAcctTitle: "",
        liabilityDeductible: "",
        liabilityLimit: "",
        trustName: "",
        prop: "",
        totalUnits: "",
        units: "",
        _isNew: true,
      },
    ]);
  }, []);

  const currentTotals = useMemo(() => {
    let totalAmount = 0;
    const uniquePropIds = new Set();
    buildingData.forEach((item) => {
      const num = parseFloat(String(item?.amount || "0").replace(/,/g, ""));
      if (!isNaN(num)) totalAmount += num;
      if (item?.propId) uniquePropIds.add(item.propId);
    });
    return {
      amount: totalAmount.toFixed(2),
      propCount: uniquePropIds.size,
      buildingCount: buildingData.length,
    };
  }, [buildingData]);

  // 表格列定义
  const columns = useMemo(() => [
    {
      title: () => <span><span style={{ color: "red" }}>*</span> Amount</span>,
      dataIndex: "amount",
      width: 120,
      render: (text, record) => (
        <Input
          prefix="$"
          value={text || ""}
          onChange={(e) => {
            const formatted = commonFn.inputAmountFormat(e.target.value, true, true);
            updateItem(record._id, { amount: formatted });
          }}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: () => <span><span style={{ color: "red" }}>*</span> Property</span>,
      dataIndex: "propId",
      width: 320,
      render: (text, record) => {
        const currentOptions = mergePropertyOptions([
          ...(propId || []),
          record.propId ? { value: record.propId, label: record.propName || String(record.propId) } : null,
        ]);
        return (
          <Select
            value={record.propId}
            options={currentOptions}
            loading={propLoading}
            showSearch
            filterOption={false}
            onSearch={onTypeSearch}
            onDropdownVisibleChange={handleDropdownOpen}
            allowClear
            placeholder="Select property"
            style={{ width: "100%" }}
            onClear={() => {
              updateItem(record._id, {
                propId: null, propName: "", glChartId: null,
                glAcctTitle: "", trustName: "", prop: "", totalUnits: "",
              });
            }}
            onChange={(value, option) => {
              updateItem(record._id, {
                propId: value,
                propName: option?.label || "",
                glChartId: null,
                glAcctTitle: "",
              });
              if (value) {
                getPropDetailsFun(value, record._id);
                fetchGlByProp(value);
              }
            }}
          />
        );
      },
    },
    {
      title: "GL / Insure Type",
      dataIndex: "glChartId",
      width: 140,
      render: (text, record) => (
        <Select
          value={record.glChartId}
          placeholder="Select GL Account"
          options={glAcctOptionsMap[record.propId] || []}
          loading={buildingLoadingMap[record._id]}
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          onChange={(value, option) => {
            updateItem(record._id, {
              glChartId: value,
              glAcctTitle: option?.label || "",
            });
          }}
        />
      ),
    },
    {
      title: "Deductible",
      dataIndex: "liabilityDeductible",
      width: 150,
      render: (text, record) => (
        <Input
          placeholder="Deductible"
          value={text || ""}
          onChange={(e) => updateItem(record._id, { liabilityDeductible: e.target.value })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Limit",
      dataIndex: "liabilityLimit",
      width: 150,
      render: (text, record) => (
        <Input
          placeholder="Limit"
          value={text || ""}
          onChange={(e) => updateItem(record._id, { liabilityLimit: e.target.value })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Entity",
      dataIndex: "trustName",
      width: 140,
      render: (text, record) => (
        <Input
          readOnly
          value={text || ""}
          suffix={buildingLoadingMap[record._id] ? <LoadingOutlined spin /> : null}
          placeholder="Auto"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Delete",
      width: 30,
      render: (_, record) => (
        buildingData.length > 1 && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => removeItem(record._id)}
          />
        )
      ),
    },
    {
  title: "View",
  width: 30,
  render: (_, record) =>
      <Button
        type="text"  
        size="small"
        style={{ color: '#1890ff' }}  
        icon={<EyeOutlined />} 
        onClick={() => {
          console.log( record);
        insuranceVPHRef.current?.setModalOpen({ record });
        }}
      />
    
}
  ], [
    propId, propLoading, glAcctOptionsMap, buildingLoadingMap,
    buildingData.length, updateItem, removeItem,
  ]);

  const addOldFn = async (files) => {
    setBtnDisabled(true);
    setUploadLoading(true);
    try {
      const currentFile = files?.[files.length - 1];
      if (!currentFile?.originFileObj) {
        return { flag: false };
      }
      const uploadedFile = await uploadByBankApi(currentFile);
      const fileData = Array.isArray(uploadedFile)
        ? uploadedFile[0]
        : uploadedFile;
      const normalizedFile = {
        ...fileData,
        uid: fileData?.uuid || currentFile.uid,
        name: fileData?.fileName || currentFile.name,
        fileName: fileData?.fileName || currentFile.name,
        allPath: fileData?.url || URL.createObjectURL(currentFile.originFileObj),
        originFileObj: currentFile.originFileObj,
        size: currentFile.size,
        status: "done",
        isFromBackend: false,
      };
      return {
        flag: true,
        fileData: normalizedFile,
      };
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Upload failed");
      return { flag: false };
    } finally {
      setBtnDisabled(false);
      setUploadLoading(false);
    }
  };

  const deleteOldFn = async (file) => {
    if (file?.isFromBackend && file?.id) {
      try {
        const res = await http.get(api.getDelInsuranceFile, {
          params: { id: file.id },
        });
        if (res?.code === "1") {
          return true;
        }
        message.error(res?.msg || "Failed to delete file");
        return false;
      } catch (error) {
        console.error(error);
        message.error("Failed to delete file");
        return false;
      }
    }
    return true;
  };

  const deleteBase64FileFn = async (file) => {
    return filePaths.filter((item) =>
      file?.uuid ? item.uuid !== file.uuid : item.uid !== file.uid,
    );
  };

  const uploadByBankApi = async (file) => {
    const formData = new FormData();
    formData.append("files", file.originFileObj);
    const res = await http.post(api.getInsuranceFile, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (res.code === "1") return res.data[0];
    throw new Error(res.msg || "Upload failed");
  };

  const syncFileList = (files = []) => {
    setFilePaths(files);
  };

  useEffect(() => {
    const normalizedFiles = (filePaths || []).map((item) => ({
      ...item,
      path: item?.path || "",
      fileName: item?.fileName || item?.name || "",
      status: item?.status ?? 1,
    }));
    setStatementFiles(normalizedFiles);
    if (normalizedFiles.length > 0) {
      setPreviewIndex(0);
    } else {
      setPreviewIndex(-1);
    }
    form.setFieldsValue({
      fileList: normalizedFiles,
    });
  }, [filePaths, form]);

  const getAgentTypeList = async () => {
    if (anetList.length > 0) return anetList;
    if (agentLoading) return [];
    setAgentLoading(true);
    try {
      const res = await http.get(api.getAgentType);
      const data = res?.data || res;
      const list = (data || []).map((item) => ({
        value: String(item.vendorId),
        label: item.vendorName,
      }));
      setAnetList(list);
      return list;
    } finally {
      setAgentLoading(false);
    }
  };

  const parseAmountValue = (value) => {
    if (value === undefined || value === null || value === "") return 0;
    const normalizedValue = String(value).replace(/,/g, "").trim();
    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const onFinish = async () => {
    setLoading(true);
    try {
      // 验证 buildingData
      for (let i = 0; i < buildingData.length; i++) {
        const item = buildingData[i];
        if (!item?.amount || parseFloat(String(item.amount).replace(/,/g, "")) === 0) {
          message.error(`Row ${i + 1}: Amount is required`);
          setLoading(false);
          return;
        }
        if (!item?.propId) {
          message.error(`Row ${i + 1}: Property is required`);
          setLoading(false);
          return;
        }
      }
      const values = form.getFieldsValue();
      const formattedBuildingList = buildingData.map((item) => {
        const buildingDataItem = {
          amount: parseAmountValue(item.amount),
          liabilityDeductible: item.liabilityDeductible,
          liabilityLimit: item.liabilityLimit,
          prop: item.prop,
          propId: item.propId,
          glChartId: item.glChartId,
        };
        if (item.id) {
          buildingDataItem.id = item.id;
        }
        if (item.pushBatch) {
          buildingDataItem.pushBatch = item.pushBatch;
        }
        return buildingDataItem;
      });

      const insuranceFileList = filePaths
        .filter((file) => file.status === "done" && !file.isFromBackend)
        .map((file) => ({
          path: file.path,
          fileName: file.fileName || file.name,
          status: 1,
        }));

      const params = {
        id: editId,
        expirationDate: values.expirationDate?.format("YYYY-MM-DD"),
        effectiveDate: values.effectiveDate?.format("YYYY-MM-DD"),
        policyNumber: values.policyNumber,
        amount: parseAmountValue(currentTotals.amount),
        numberOfProperties: currentTotals.propCount,
        numberOfBuildings: currentTotals.buildingCount,
        carrier: values.carrier,
        agentVendorId: values.agent ? Number(values.agent) : undefined,
        payeeVendorId: values.payee ? Number(values.payee) : undefined,
        buildingList: formattedBuildingList,
        insuranceFileList,
      };

      const res = await http.post(api.getUpdateInsuranceByBuilding, params);

      if (res?.code === "1") {
        message.success("Insurance updated successfully.");
        setTimeout(() => {
          try {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage(
                {
                  type: "INSURANCE_REFRESH",
                  id: editId,
                },
                "*"
              );
            }
            window.close();
            setTimeout(() => {
              if (!window.closed) {
                window.location.href = "about:blank";
              }
            }, 300);
          } catch (e) {
            console.error("close window error:", e);
            window.location.href = "about:blank";
          }
        }, 800);
      } else {
        message.error(res?.msg);
      }
    } catch (err) {
      console.error(err);
      if (!err?.errorFields) {
        message.error("Request failed, please try again");
      }
    } finally {
      setLoading(false);
    }
  };

  // 更新表单中的汇总值
  useEffect(() => {
    form.setFieldsValue({
      amount: commonFn.inputAmountFormat(currentTotals.amount, false, true),
      numberOfProperties: currentTotals.propCount,
      numberOfBuildings: currentTotals.buildingCount,
    });
  }, [currentTotals, form]);

  return (
    <div>
      <Spin spinning={loading} tip="Loading...">
        <div style={{ background: "#fff", borderRadius: 8 }}>
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 10 }}
            wrapperCol={{ span: 14 }}
            onFinish={onFinish}
          >
            <Row gutter={24} style={{ margin: 0 }}>
              <Col span={4}>
                <div style={columnStyle}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                    <BankOutlined /> Insurance Detail
                  </div>
                  <Form.Item name="expirationDate" label="Expiration Date">
                    <DatePicker style={{ width: "100%" }} placeholder="YYYY-MM-DD" />
                  </Form.Item>
                  <Form.Item name="effectiveDate" label="Effective Date">
                    <DatePicker style={{ width: "100%" }} placeholder="YYYY-MM-DD" />
                  </Form.Item>
                  <Form.Item name="policyNumber" label="Policy Number">
                    <Input placeholder="Policy Number" />
                  </Form.Item>
                  <Form.Item name="carrier" label="Carrier">
                    <Input placeholder="carrier" />
                  </Form.Item>
                  <Form.Item name="agent" label="Agent" rules={[{ required: true }]}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Please select Agent"
                      options={anetList}
                      loading={agentLoading}
                      showSearch
                      optionFilterProp="label"
                      onDropdownVisibleChange={(open) => open && getAgentTypeList()}
                    />
                  </Form.Item>
                  <Form.Item name="payee" label="Payee" rules={[{ required: true }]}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Please select Payee"
                      options={anetList}
                      loading={agentLoading}
                      showSearch
                      optionFilterProp="label"
                      onDropdownVisibleChange={(open) => open && getAgentTypeList()}
                    />
                  </Form.Item>
                  <Form.Item name="amount" label="Total Amount">
                    <Input prefix="$" disabled />
                  </Form.Item>
                  <Form.Item name="numberOfProperties" label="# of Properties">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item name="numberOfBuildings" label="# of Buildings">
                    <Input disabled />
                  </Form.Item>
                </div>
              </Col>

              <Col span={20}>
                <div style={panelStyle}>
                  
                  <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>Building List</div>
                        <Button
                          type="primary"
                          loading={loading || uploadLoading}
                          htmlType="submit"
                        >
                          Submit - Update Current Policy Info
                        </Button>
                      </div>
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        onClick={() => setUploadOpen(true)}
                        icon={<CloudUploadOutlined />}
                      >
                      View Upload File
                      </Button>
                    </Col>
                  </Row>

                  {/* 表格区域 */}
                  <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                    <Table
                      rowClassName="table-row-gradient"
                      columns={columns}
                      dataSource={buildingData.filter(item => !item._isNew)}
                      rowKey="_id"
                      className="no-border-table"
                      bordered={false}
                      showHeader={true}
                      sticky={{ offsetHeader: 0 }}
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        defaultPageSize: 50,
                        pageSizeOptions: ["30", "50", "100", "200"],
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} items`,
                        position: ["bottomRight"],
                      }}
                      locale={{ emptyText: "No existing buildings." }}
                    />

                    {buildingData.filter(item => item._isNew).length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ color: "#1677ff", fontSize: 14, fontWeight: 600, marginBottom: 8, paddingLeft: 8 }}>
                          New Building
                        </div>
                        <Table
                          rowClassName="table-row-gradient"
                          columns={columns}
                          dataSource={buildingData.filter(item => item._isNew)}
                          className="no-border-table"
                          rowKey="_id"
                          bordered={false}
                          showHeader={false}
                          pagination={false}
                          locale={{ emptyText: "" }}
                          scroll={{ x: 'max-content' }}
                        />
                      </div>
                    )}

                    <Button
                      type="dashed"
                      block
                      style={{ marginTop: 12 }}
                      onClick={addItem}
                      icon={<PlusOutlined />}
                    >
                      Add Building
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </Spin>

      <Drawer
        title="Upload PDF / Image"
        placement="right"
        width="40%"
        open={uploadOpen}
        onClose={() => {
          if (uploadLoading) return;
          setUploadOpen(false);
        }}
        maskClosable={!uploadLoading}
        closable={!uploadLoading}
        destroyOnClose={false}
      >
        <div style={{ margin: 20 }}>
          <div className="uploadText">
            Uploaded files only support JPG, PNG or PDF.
          </div>
          <div style={{ marginTop: 20 }}>
            <Form.Item name="new_insurance" noStyle>
              <CustomUploadNewBank
                ref={uploadRef}
                customConfig={{
                  uploadButtonStyle: {
                    top: "20px",
                    right: "20px",
                  },
                  pdfHeight: "80vh",
                  fileFullName: "name",
                  notMultiple: true,
                  accept: ".jpg, .jpeg, .png, .pdf",
                  status: "add",
                  deleteOldFn,
                  deleteBase64FileFn,
                  addOldFn,
                }}
                setFilePaths={syncFileList}
                filePaths={filePaths}
                setBtnDisabled={setBtnDisabled}
                btnDisabled={btnDisabled}
              />
            </Form.Item>
            <Form.Item name="fileList" hidden />
          </div>
        </div>
      </Drawer>
        <InsuranceVPH ref={insuranceVPHRef} />
    </div>
  );
};

export default InsuranceViewDetail;